import { useState } from "react";
import "./ModalStyles.css";
import { saveImagemCarteirinhaApi, saveComprovanteMatriculaApi, updateCarteirinhaApi } from "../../Api";
import { notify } from "../Notify";
import DropFixImage from "../DropFixImage";
import ComprovanteUpload from "../ComprovanteUpload";
import { escolaridadeAll } from "../../utils";

const ModalDadosDigital = ({ carteirinhaId, onSuccess, reprovada = false, motivoReprovacao = null, dadosAtuais = null }) => {
    const [loading, setLoading] = useState(false);
    // Pré-preenche com os dados atuais da carteirinha (no reenvio após reprovação
    // o aluno só corrige o que estiver errado). A foto sempre recomeça vazia.
    const [form, setForm] = useState(() => ({
        instituicao: dadosAtuais?.instituicao || '',
        curso: dadosAtuais?.curso || '',
        escolaridade: dadosAtuais?.escolaridade || '',
        foto: null,
        comprovante: null, // { arquivo, mimeType }
    }));

    // O motivo vem como texto único (motivos separados por "; "). Quebramos em
    // lista pra exibir de forma legível.
    const motivos = (motivoReprovacao || '')
        .split(';')
        .map((m) => m.trim())
        .filter(Boolean);

    const handleSubmit = async () => {
        if (!form.instituicao || !form.curso || !form.escolaridade || !form.foto) {
            notify("Preencha todos os campos e anexe sua foto.", "warning");
            return;
        }
        if (!form.comprovante) {
            notify("Anexe o comprovante de matrícula (PDF ou imagem).", "warning");
            return;
        }

        setLoading(true);

        try {
            const dadosCarteirinha = {
                instituicao: form.instituicao,
                curso: form.curso,
                escolaridade: form.escolaridade,
                status_cadastro: 'completo'
            };

            const resUpdate = await updateCarteirinhaApi({ carteirinhaId, dadosCarteirinha });
            const idConfirmado = resUpdate.data.carteirinha_id || carteirinhaId;
            const ano = new Date().getFullYear().toString();

            // Foto (já recortada/comprimida pelo DropFixImage) + comprovante.
            // Backend faz upsert nas duas tabelas — reenvio após reprovação
            // sobrescreve o que tinha antes.
            await saveImagemCarteirinhaApi({
                idDaCarteirinha: idConfirmado,
                imagem: form.foto,
                ano,
            });
            await saveComprovanteMatriculaApi({
                idDaCarteirinha: idConfirmado,
                arquivo: form.comprovante.arquivo,
                mimeType: form.comprovante.mimeType,
                ano,
            });

            onSuccess();
        } catch (err) {
            console.error("Erro no salvamento:", err);
            notify(err.response?.data?.message || "Erro ao salvar dados. Verifique sua conexão.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-container glass">
                <div className="modal-header">
                    <h2>{reprovada ? 'Corrija sua carteirinha' : 'Dados da Carteirinha'}</h2>
                    <p>
                        {reprovada
                            ? 'Sua carteirinha foi reprovada. Ajuste o que for necessário e reenvie.'
                            : 'Preencha as informações para emissão digital'}
                    </p>
                </div>

                {reprovada && (
                    <div style={bannerReprovado.box}>
                        <div style={bannerReprovado.titulo}>⚠️ Motivo da reprovação</div>
                        {motivos.length > 0 ? (
                            <ul style={bannerReprovado.lista}>
                                {motivos.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        ) : (
                            <p style={bannerReprovado.texto}>
                                Revise seus dados e a foto antes de reenviar.
                            </p>
                        )}
                    </div>
                )}

                <div className="modal-body">
                    <div className="input-group">
                        <label>Instituição de Ensino</label>
                        <input
                            type="text"
                            placeholder="Ex: UFMG, USP, FGV..."
                            value={form.instituicao}
                            onChange={e => setForm({ ...form, instituicao: e.target.value })}
                        />
                    </div>

                    <div className="input-group">
                        <label>Nome do Curso</label>
                        <input
                            type="text"
                            placeholder="Ex: Administração"
                            value={form.curso}
                            onChange={e => setForm({ ...form, curso: e.target.value })}
                        />
                    </div>

                    <div className="input-group">
                        <label>Escolaridade</label>
                        <select
                            value={form.escolaridade}
                            onChange={e => setForm({ ...form, escolaridade: e.target.value })}
                        >
                            <option value="">Selecione o nível</option>
                            {escolaridadeAll.map((e) => (
                                <option key={e.id} value={e.id}>
                                    {e.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="input-group photo-upload">
                        <label>Sua foto</label>
                        <DropFixImage
                            value={form.foto}
                            onChange={(base64) => setForm({ ...form, foto: base64 })}
                        />
                    </div>

                    <div className="input-group">
                        <label>Comprovante de matrícula</label>
                        <ComprovanteUpload
                            value={form.comprovante}
                            onChange={(v) => setForm({ ...form, comprovante: v })}
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        className="btn-primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading
                            ? 'Processando...'
                            : (reprovada ? 'Reenviar para análise' : 'Confirmar e Continuar')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const bannerReprovado = {
    box: {
        margin: '0 0 4px',
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        borderLeft: '4px solid #EF4444',
        borderRadius: 10,
        padding: '12px 14px',
    },
    titulo: {
        fontWeight: 700,
        color: '#991B1B',
        fontSize: '0.9rem',
        marginBottom: 6,
    },
    lista: {
        margin: 0,
        paddingLeft: 18,
        color: '#B91C1C',
        fontSize: '0.85rem',
        lineHeight: 1.5,
    },
    texto: {
        margin: 0,
        color: '#B91C1C',
        fontSize: '0.85rem',
        lineHeight: 1.5,
    },
};

export default ModalDadosDigital;